import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { MessageCircle, Heart, Trash2, ImageIcon, X, Loader2 } from 'lucide-react';
import { getImageUrl } from '../../utils/imageUtils';

const API = process.env.REACT_APP_BACKEND_URL;

export const GroupFeedTab = ({
  group, user, posts,
  newPost, setNewPost,
  postImage, postImagePreview, setPostImage, setPostImagePreview,
  posting, uploadingImage,
  onCreatePost, onPhotoSelect, onLikePost, onDeletePost,
}) => {
  const photoInputRef = useRef(null);

  return (
    <motion.div
      key="feed"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="max-w-2xl mx-auto"
    >
      {group.is_member && (
        <form onSubmit={onCreatePost} className="bg-card border border-border p-4 mb-6">
          <Textarea
            placeholder="Share something with the group..."
            value={newPost}
            onChange={(e) => setNewPost(e.target.value)}
            className="bg-black border-border mb-3"
            rows={3}
            data-testid="group-post-textarea"
          />

          {postImagePreview && (
            <div className="relative mb-3 inline-block">
              <img src={postImagePreview} alt="Preview" className="max-h-40 rounded border border-border" />
              <button
                type="button"
                onClick={() => { setPostImage(null); setPostImagePreview(''); }}
                className="absolute -top-2 -right-2 h-6 w-6 bg-red-500 rounded-full flex items-center justify-center"
              >
                <X className="h-3 w-3 text-white" />
              </button>
            </div>
          )}

          <div className="flex justify-between items-center">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => photoInputRef.current?.click()}
              className="text-muted-foreground hover:text-primary"
              data-testid="group-add-photo-btn"
            >
              <ImageIcon className="h-4 w-4 mr-2" />
              Add Photo
            </Button>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              onChange={onPhotoSelect}
              className="hidden"
            />
            <Button
              type="submit"
              disabled={posting || (!newPost.trim() && !postImage)}
              className="bg-primary text-black"
              data-testid="group-post-submit-btn"
            >
              {posting ? (uploadingImage ? 'Uploading...' : <Loader2 className="h-4 w-4 animate-spin" />) : 'Post'}
            </Button>
          </div>
        </form>
      )}

      {posts.length === 0 ? (
        <div className="text-center py-16 bg-card border border-border">
          <MessageCircle className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-heading text-xl text-white mb-2">No Posts Yet</h3>
          <p className="text-muted-foreground">Be the first to share something!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map((post) => (
            <div key={post.id} className="bg-card border border-border p-4" data-testid="group-post">
              <div className="flex items-start gap-3 mb-3">
                <Link to={`/user/${post.user?.id}`}>
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={getImageUrl(post.user?.profile_picture)} />
                    <AvatarFallback className="bg-primary/20 text-primary">
                      {post.user?.name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                </Link>
                <div className="flex-1">
                  <Link to={`/user/${post.user?.id}`} className="font-medium text-white hover:text-primary">
                    {post.user?.name}
                  </Link>
                  <p className="text-xs text-muted-foreground">
                    {new Date(post.created_at).toLocaleDateString()}
                  </p>
                </div>
                {post.user_id === user?.id && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDeletePost(post.id)}
                    className="text-muted-foreground hover:text-red-400"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <p className="text-white whitespace-pre-wrap mb-3">{post.content}</p>
              {post.image_url && (
                <img
                  src={post.image_url.startsWith('http') ? post.image_url : `${API}${post.image_url}`}
                  alt="Post"
                  className="w-full rounded border border-border mb-3 max-h-96 object-cover"
                />
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onLikePost(post.id)}
                className={post.user_liked ? 'text-red-400' : 'text-muted-foreground'}
              >
                <Heart className={`h-4 w-4 mr-1 ${post.user_liked ? 'fill-current' : ''}`} />
                {post.likes_count || 0}
              </Button>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
};
